import { useQuery } from '@tanstack/react-query'

import { dataOrThrow } from '@snootsvet/shared/portal'

import { usePublicBookingApi } from '@/customer/hooks/use-api'

/**
 * React Query key factory for the public booking-context read. Keyed
 * by token so two tabs opened with different signed links can't share
 * a cache entry. Exported so mutation hooks can invalidate the
 * matching context.
 */
export const publicBookingContextQueryKey = (token: string | null) =>
  ['public-booking-context', token] as const

/**
 * Loads the signed-link booking context via
 * `GET /api/public/booking-context`. Authenticated by the token
 * that arrived in the URL, forwarded via `usePublicBookingApi`.
 *
 * Query stays disabled until a token is provided so pages can mount
 * the hook before the URL param has been read.
 */
export function usePublicBookingContext(token: string | null) {
  const api = usePublicBookingApi(token)
  return useQuery({
    queryKey: publicBookingContextQueryKey(token),
    enabled: !!token,
    queryFn: async () => dataOrThrow(await api.get('/public/booking-context')),
  })
}
